import {Injectable} from '@angular/core';
import {HttpHeaders} from '@angular/common/http';
import {
  ApiCollection,
  RiskDeclarationRow,
  ApiResponse,
  RiskConceptRow,
  RiskDataSourceRow,
  RiskCrimeRow,
  RiskHeatmap,
  RiskHeatmapPoint,
  RiskImpactRow,
  RiskTableCrime,
  RiskTableRow,
  RiskVariableRow
} from '@core/types';
import {ENDPOINTS, GET_URL, log} from '@core/utils';
import {HttpService} from '@core/services';
import {CompanyStateService} from '@core/states';
import {AuthStateService} from '@auth/states';

@Injectable({
  providedIn: 'root'
})
export class RiskService {
  private readonly defaultHeatmapLabels = {x: 'Impacto', y: 'Probabilidad'};
  private readonly defaultHeatmapAttributes = {
    x: ['Muy bajo', 'Bajo', 'Medio', 'Alto', 'Muy alto'],
    y: ['Muy bajo', 'Bajo', 'Medio', 'Alto', 'Muy alto'],
  };

  constructor(
    private apiService: HttpService,
    private companyState: CompanyStateService,
    private authState: AuthStateService
  ) {}

  async getHeatmap(type: 'inherent' | 'residual' = 'inherent', options?: {skipLoader?: boolean}): Promise<ApiResponse<RiskHeatmap>> {
    try {
      const url = GET_URL(ENDPOINTS.RISK_HEATMAP, {type});
      const headers = this.buildHeaders(options?.skipLoader);
      const response: any = await this.apiService.get(url, headers ? {headers} : undefined);
      return {success: true, result: this.mapHeatmap(response)};
    } catch (error: any) {
      log.error('Error loading risk heatmap:', error);
    }
    return {success: false, message: 'No se pudo cargar el mapa de calor.'};
  }

  async getRiskTable(
    type: 'inherent' | 'residual' = 'inherent',
    options?: {skipLoader?: boolean}
  ): Promise<ApiResponse<{crimes: RiskTableCrime[]; rows: RiskTableRow[]}>> {
    try {
      const url = GET_URL(ENDPOINTS.RISK_TABLE, {type});
      const headers = this.buildHeaders(options?.skipLoader);
      const response: {crimes: RiskTableCrime[]; rows: RiskTableRow[]} = await this.apiService.get(url, headers ? {headers} : undefined);
      return {success: true, result: response};
    } catch (error: any) {
      log.error('Error loading risk table:', error);
    }
    return {success: false, message: 'No se pudo cargar la tabla de riesgo.'};
  }

  async getCrimes(page = 1, itemsPerPage = 100, options?: {skipLoader?: boolean}): Promise<ApiResponse<RiskCrimeRow[]>> {
    try {
      const url = GET_URL(ENDPOINTS.CRIMES, {page, itemsPerPage});
      const headers = this.buildHeaders(options?.skipLoader);
      const response: ApiCollection<any> = await this.apiService.get(url, headers ? {headers} : undefined);
      const crimes = this.apiService.normalizeCollection<any>(response).member.map((item: any) => this.mapCrime(item));
      return {success: true, result: crimes};
    } catch (error: any) {
      log.error('Error loading crimes:', error);
    }
    return {success: false, message: 'No se pudieron cargar los delitos.'};
  }

  async createCrime(payload: {name: string; article?: string; description?: string}, options?: {skipLoader?: boolean}): Promise<ApiResponse<RiskCrimeRow>> {
    try {
      const url = GET_URL(ENDPOINTS.CRIMES);
      const headers = this.buildHeaders(options?.skipLoader);
      const response: any = await this.apiService.post(url, {
        name: payload.name,
        article: payload.article || null,
        description: payload.description || null,
      }, headers ? {headers} : undefined);
      return {success: true, result: this.mapCrime(response)};
    } catch (error: any) {
      log.error('Error creating crime:', error);
    }
    return {success: false, message: 'No se pudo crear el delito.'};
  }

  async updateCrime(
    crimeId: string | number,
    payload: {name?: string; article?: string; description?: string},
    options?: {skipLoader?: boolean}
  ): Promise<ApiResponse<RiskCrimeRow>> {
    try {
      const url = GET_URL(ENDPOINTS.CRIME_DETAIL, {id: crimeId});
      const baseHeaders = this.buildHeaders(options?.skipLoader) ?? new HttpHeaders();
      const headers = baseHeaders.set('Content-Type', 'application/merge-patch+json');
      const body: Record<string, any> = {};
      if (payload.name !== undefined) body['name'] = payload.name;
      if (payload.article !== undefined) body['article'] = payload.article || null;
      if (payload.description !== undefined) body['description'] = payload.description || null;
      const response: any = await this.apiService.patch(url, body, {headers, json: false});
      return {success: true, result: this.mapCrime(response)};
    } catch (error: any) {
      log.error('Error updating crime:', error);
    }
    return {success: false, message: 'No se pudo actualizar el delito.'};
  }

  async deleteCrime(crimeId: string | number, options?: {skipLoader?: boolean}): Promise<ApiResponse<undefined>> {
    try {
      const url = GET_URL(ENDPOINTS.CRIME_DETAIL, {id: crimeId});
      const headers = this.buildHeaders(options?.skipLoader);
      await this.apiService.delete(url, headers ? {headers} : undefined);
      return {success: true};
    } catch (error: any) {
      log.error('Error deleting crime:', error);
    }
    return {success: false, message: 'No se pudo eliminar el delito.'};
  }

  async getImpacts(page = 1, itemsPerPage = 200, options?: {skipLoader?: boolean}): Promise<ApiResponse<RiskImpactRow[]>> {
    try {
      const url = GET_URL(ENDPOINTS.RISK_IMPACTS, {page, itemsPerPage});
      const headers = this.buildHeaders(options?.skipLoader);
      const response: ApiCollection<any> = await this.apiService.get(url, headers ? {headers} : undefined);
      const impacts = this.apiService.normalizeCollection<any>(response).member.map((item: any) => this.mapImpact(item));
      return {success: true, result: impacts};
    } catch (error: any) {
      log.error('Error loading impacts:', error);
    }
    return {success: false, message: 'No se pudieron cargar los impactos.'};
  }

  async createImpact(payload: {
    conceptId: string | number;
    crimeId: string | number;
    punitive: string;
    operative: string;
    reputational: string;
    financial: string;
  }, options?: {skipLoader?: boolean}): Promise<ApiResponse<RiskImpactRow>> {
    try {
      const url = GET_URL(ENDPOINTS.RISK_IMPACTS);
      const headers = this.buildHeaders(options?.skipLoader);
      const response: any = await this.apiService.post(url, {
        concept: this.toConceptIri(payload.conceptId),
        crime: this.toCrimeIri(payload.crimeId),
        punitive: payload.punitive,
        operative: payload.operative,
        reputational: payload.reputational,
        financial: payload.financial,
      }, headers ? {headers} : undefined);
      return {success: true, result: this.mapImpact(response)};
    } catch (error: any) {
      log.error('Error creating impact:', error);
    }
    return {success: false, message: 'No se pudo crear el impacto.'};
  }

  async updateImpact(impactId: string | number, payload: {
    conceptId?: string | number;
    crimeId?: string | number;
    punitive?: string;
    operative?: string;
    reputational?: string;
    financial?: string;
  }, options?: {skipLoader?: boolean}): Promise<ApiResponse<RiskImpactRow>> {
    try {
      const url = GET_URL(ENDPOINTS.RISK_IMPACT_DETAIL, {id: impactId});
      const baseHeaders = this.buildHeaders(options?.skipLoader) ?? new HttpHeaders();
      const headers = baseHeaders.set('Content-Type', 'application/merge-patch+json');
      const body: Record<string, any> = {};
      if (payload.conceptId !== undefined) body['concept'] = this.toConceptIri(payload.conceptId);
      if (payload.crimeId !== undefined) body['crime'] = this.toCrimeIri(payload.crimeId);
      if (payload.punitive !== undefined) body['punitive'] = payload.punitive;
      if (payload.operative !== undefined) body['operative'] = payload.operative;
      if (payload.reputational !== undefined) body['reputational'] = payload.reputational;
      if (payload.financial !== undefined) body['financial'] = payload.financial;
      const response: any = await this.apiService.patch(url, body, {headers, json: false});
      if (!response) return {success: true};
      return {success: true, result: this.mapImpact(response)};
    } catch (error: any) {
      log.error('Error updating impact:', error);
    }
    return {success: false, message: 'No se pudo actualizar el impacto.'};
  }

  async deleteImpact(impactId: string | number, options?: {skipLoader?: boolean}): Promise<ApiResponse<undefined>> {
    try {
      const url = GET_URL(ENDPOINTS.RISK_IMPACT_DETAIL, {id: impactId});
      const headers = this.buildHeaders(options?.skipLoader);
      await this.apiService.delete(url, headers ? {headers} : undefined);
      return {success: true};
    } catch (error: any) {
      if (Number(error?.status) === 404 || Number(error?.details?.status) === 404) {
        // Idempotent delete: if the record no longer exists, consider it deleted for UI flow.
        return {success: true};
      }
      log.error('Error deleting impact:', error);
    }
    return {success: false, message: 'No se pudo eliminar el impacto.'};
  }

  async getConcepts(page = 1, itemsPerPage = 50, options?: {skipLoader?: boolean}): Promise<ApiResponse<RiskConceptRow[]>> {
    try {
      const url = GET_URL(ENDPOINTS.RISK_CONCEPTS, {page, itemsPerPage});
      const headers = this.buildHeaders(options?.skipLoader);
      const response: ApiCollection<any> = await this.apiService.get(url, headers ? {headers} : undefined);
      const concepts = this.apiService.normalizeCollection<any>(response).member.map((item: any) => this.mapConcept(item));
      return {success: true, result: concepts};
    } catch (error: any) {
      log.error('Error loading risk concepts:', error);
    }
    return {success: false, message: 'No se pudieron cargar los conceptos.'};
  }

  async getDeclarations(page = 1, itemsPerPage = 50, options?: {skipLoader?: boolean}): Promise<ApiResponse<RiskDeclarationRow[]>> {
    try {
      const url = GET_URL(ENDPOINTS.DECLARATIONS, {page, itemsPerPage});
      const headers = this.buildHeaders(options?.skipLoader);
      const response: ApiCollection<any> = await this.apiService.get(url, headers ? {headers} : undefined);
      const declarations = this.apiService.normalizeCollection<any>(response).member.map((item: any) => this.mapDeclaration(item));
      return {success: true, result: declarations};
    } catch (error: any) {
      log.error('Error loading declarations:', error);
    }
    return {success: false, message: 'No se pudieron cargar las declaraciones.'};
  }

  async createDeclaration(
    payload: {
      name: string;
      description?: string;
      type: 'boolean' | 'numeric';
      variableName?: string;
      comparator?: 'greater_than' | 'less_than' | 'equal' | 'less_or_equal' | 'greater_or_equal' | 'not_equal';
      threshold?: string | number;
      booleanAnswer?: 'yes' | 'no';
    },
    options?: {skipLoader?: boolean}
  ): Promise<ApiResponse<RiskDeclarationRow>> {
    try {
      const url = GET_URL(ENDPOINTS.DECLARATIONS);
      const headers = this.buildHeaders(options?.skipLoader);
      const body: Record<string, any> = {
        name: payload.name,
        description: payload.description || '',
        type: payload.type,
      };
      if (payload.variableName !== undefined) body['variableName'] = payload.variableName;
      if (payload.type === 'numeric') {
        body['comparator'] = payload.comparator;
        body['threshold'] = payload.threshold !== undefined ? String(payload.threshold) : undefined;
      }
      if (payload.type === 'boolean') {
        body['booleanAnswer'] = payload.booleanAnswer;
      }
      const response: any = await this.apiService.post(url, body, headers ? {headers} : undefined);
      return {success: true, result: this.mapDeclaration(response)};
    } catch (error: any) {
      log.error('Error creating declaration:', error);
    }
    return {success: false, message: 'No se pudo crear la declaración.'};
  }

  async updateDeclaration(
    declarationId: string | number,
    payload: {
      name?: string;
      description?: string;
      type?: 'boolean' | 'numeric';
      variableName?: string;
      comparator?: 'greater_than' | 'less_than' | 'equal' | 'less_or_equal' | 'greater_or_equal' | 'not_equal';
      threshold?: string | number;
      booleanAnswer?: 'yes' | 'no';
    },
    options?: {skipLoader?: boolean}
  ): Promise<ApiResponse<RiskDeclarationRow>> {
    try {
      const url = GET_URL(ENDPOINTS.DECLARATION_DETAIL, {id: declarationId});
      const baseHeaders = this.buildHeaders(options?.skipLoader) ?? new HttpHeaders();
      const headers = baseHeaders.set('Content-Type', 'application/merge-patch+json');
      const body: Record<string, any> = {};
      if (payload.name !== undefined) body['name'] = payload.name;
      if (payload.description !== undefined) body['description'] = payload.description;
      if (payload.type !== undefined) body['type'] = payload.type;
      if (payload.variableName !== undefined) body['variableName'] = payload.variableName;
      if (payload.comparator !== undefined) body['comparator'] = payload.comparator;
      if (payload.threshold !== undefined) body['threshold'] = String(payload.threshold);
      if (payload.booleanAnswer !== undefined) body['booleanAnswer'] = payload.booleanAnswer;
      const response: any = await this.apiService.patch(url, body, {headers, json: false});
      if (!response) {
        return {success: true};
      }
      return {success: true, result: this.mapDeclaration(response)};
    } catch (error: any) {
      log.error('Error updating declaration:', error);
    }
    return {success: false, message: 'No se pudo actualizar la declaración.'};
  }

  async deleteDeclaration(declarationId: string | number, options?: {skipLoader?: boolean}): Promise<ApiResponse<undefined>> {
    try {
      const url = GET_URL(ENDPOINTS.DECLARATION_DETAIL, {id: declarationId});
      const headers = this.buildHeaders(options?.skipLoader);
      await this.apiService.delete(url, headers ? {headers} : undefined);
      return {success: true};
    } catch (error: any) {
      log.error('Error deleting declaration:', error);
    }
    return {success: false, message: 'No se pudo eliminar la declaración.'};
  }

  async getConcept(conceptId: string | number): Promise<ApiResponse<RiskConceptRow>> {
    try {
      const url = GET_URL(ENDPOINTS.RISK_CONCEPT_DETAIL, {id: conceptId});
      const headers = this.buildHeaders();
      const response: any = await this.apiService.get(url, headers ? {headers} : undefined);
      return {success: true, result: this.mapConcept(response)};
    } catch (error: any) {
      log.error('Error loading risk concept detail:', error);
    }
    return {success: false, message: 'No se pudo cargar el concepto.'};
  }

  async createConcept(
    payload: {name: string; description?: string},
    options?: {skipLoader?: boolean}
  ): Promise<ApiResponse<RiskConceptRow>> {
    try {
      const url = GET_URL(ENDPOINTS.RISK_CONCEPTS);
      const headers = this.buildHeaders(options?.skipLoader);
      const body: Record<string, any> = {
        name: payload.name,
        description: payload.description,
      };
      const response: any = await this.apiService.post(url, body, headers ? {headers} : undefined);
      return {success: true, result: this.mapConcept(response)};
    } catch (error: any) {
      log.error('Error creating risk concept:', error);
    }
    return {success: false, message: 'No se pudo crear el concepto.'};
  }

  async updateConcept(
    conceptId: string | number,
    payload: {name?: string; description?: string},
    options?: {skipLoader?: boolean}
  ): Promise<ApiResponse<RiskConceptRow>> {
    try {
      const url = GET_URL(ENDPOINTS.RISK_CONCEPT_DETAIL, {id: conceptId});
      const baseHeaders = this.buildHeaders(options?.skipLoader) ?? new HttpHeaders();
      const headers = baseHeaders.set('Content-Type', 'application/merge-patch+json');
      const body: Record<string, any> = {};
      if (payload.name !== undefined) body['name'] = payload.name;
      if (payload.description !== undefined) body['description'] = payload.description;
      const response: any = await this.apiService.patch(url, body, {headers, json: false});
      if (!response) {
        return {success: true};
      }
      return {success: true, result: this.mapConcept(response)};
    } catch (error: any) {
      log.error('Error updating risk concept:', error);
    }
    return {success: false, message: 'No se pudo actualizar el concepto.'};
  }

  async deleteConcept(conceptId: string | number, options?: {skipLoader?: boolean}): Promise<ApiResponse<undefined>> {
    try {
      const url = GET_URL(ENDPOINTS.RISK_CONCEPT_DETAIL, {id: conceptId});
      const headers = this.buildHeaders(options?.skipLoader);
      await this.apiService.delete(url, headers ? {headers} : undefined);
      return {success: true};
    } catch (error: any) {
      log.error('Error deleting risk concept:', error);
      return {success: false, message: error?.message || 'No se pudo eliminar el concepto.'};
    }
  }

  async evaluateConceptsBatch(
    conceptIds: Array<string | number>,
    context?: Record<string, any>,
    options?: {skipLoader?: boolean}
  ): Promise<ApiResponse<Array<{
    conceptId: string | number;
    totalScore: number;
    variables?: Array<{variableId: string | number; activated: boolean}>;
    success: boolean;
    error?: string;
  }>>> {
    try {
      const url = GET_URL(ENDPOINTS.RISK_CONCEPTS_EVALUATE_BATCH);
      const headers = this.buildHeaders(options?.skipLoader);
      const payload: Record<string, any> = {conceptIds};
      if (context) {
        payload['context'] = context;
      }
      const response: any = await this.apiService.post(url, payload, headers ? {headers} : undefined);
      const items = Array.isArray(response?.items) ? response.items : [];
      return {success: true, result: items};
    } catch (error: any) {
      log.error('Error evaluating risk concepts batch:', error);
    }

    return {success: false, message: 'No se pudieron evaluar los conceptos.'};
  }

  async getDataSources(options?: {skipLoader?: boolean}): Promise<ApiResponse<RiskDataSourceRow[]>> {
    try {
      const url = GET_URL(ENDPOINTS.RISK_DATA_SOURCES, {pagination: 'false'});
      const headers = this.buildHeaders(options?.skipLoader);
      const response: ApiCollection<any> = await this.apiService.get(url, headers ? {headers} : undefined);
      const dataSources = this.apiService.normalizeCollection<any>(response).member.map((item: any) => ({
        id: item.id,
        name: item.name || item.title || item.code || `Origen ${item.id}`,
        variableCount: Array.isArray(item.variables) ? item.variables.length : 0,
      }));
      return {success: true, result: dataSources};
    } catch (error: any) {
      log.error('Error loading risk data sources:', error);
    }
    return {success: false, message: 'No se pudieron cargar los orígenes de datos.'};
  }

  async getVariables(
    page = 1,
    itemsPerPage = 50,
    options?: {skipLoader?: boolean; conceptId?: string | number}
  ): Promise<ApiResponse<RiskVariableRow[]>> {
    try {
      const params: Record<string, string | number> = {page, itemsPerPage};
      if (options?.conceptId !== undefined && options?.conceptId !== null) {
        params['rules.concept.id'] = options.conceptId;
      }
      const url = GET_URL(ENDPOINTS.RISK_VARIABLES, params);
      const headers = this.buildHeaders(options?.skipLoader);
      const response: ApiCollection<any> = await this.apiService.get(url, headers ? {headers} : undefined);
      const variables = this.apiService.normalizeCollection<any>(response).member.map((item: any) => this.mapVariable(item));
      return {success: true, result: variables};
    } catch (error: any) {
      log.error('Error loading risk variables:', error);
    }
    return {success: false, message: 'No se pudieron cargar las variables.'};
  }

  async getVariable(variableId: string | number, options?: {skipLoader?: boolean}): Promise<ApiResponse<RiskVariableRow>> {
    try {
      const url = GET_URL(ENDPOINTS.RISK_VARIABLE_DETAIL, {id: variableId});
      const headers = this.buildHeaders(options?.skipLoader);
      const response: any = await this.apiService.get(url, headers ? {headers} : undefined);
      return {success: true, result: this.mapVariable(response)};
    } catch (error: any) {
      log.error('Error loading risk variable detail:', error);
    }
    return {success: false, message: 'No se pudo cargar la variable.'};
  }

  async createVariable(
    payload: {
      name: string;
      conceptId: string | number;
      comparator: 'greater_than' | 'less_than' | 'equal' | 'less_or_equal' | 'greater_or_equal' | 'not_equal';
      threshold: string | number;
      weight: string | number;
      dataSourceId?: string | number;
      conceptRules?: Array<{
        conceptId: string | number;
        comparator: 'greater_than' | 'less_than' | 'equal' | 'less_or_equal' | 'greater_or_equal' | 'not_equal';
        threshold: string | number;
        weight: string | number;
      }>;
    },
    options?: {skipLoader?: boolean}
  ): Promise<ApiResponse<RiskVariableRow>> {
    try {
      const url = GET_URL(ENDPOINTS.RISK_VARIABLES);
      const headers = this.buildHeaders(options?.skipLoader);
      const body: Record<string, any> = {
        name: payload.name,
        concept: this.toConceptIri(payload.conceptId),
        comparator: payload.comparator,
        threshold: String(payload.threshold),
        weight: String(payload.weight),
      };
      if (Array.isArray(payload.conceptRules)) {
        body['conceptRules'] = payload.conceptRules.map(rule => ({
          conceptId: rule.conceptId,
          comparator: rule.comparator,
          threshold: String(rule.threshold),
          weight: String(rule.weight),
        }));
      }
      if (payload.dataSourceId) {
        body['dataSource'] = this.toDataSourceIri(payload.dataSourceId);
      }
      const response: any = await this.apiService.post(url, body, headers ? {headers} : undefined);
      return {success: true, result: this.mapVariable(response)};
    } catch (error: any) {
      log.error('Error creating risk variable:', error);
    }
    return {success: false, message: 'No se pudo crear la variable.'};
  }

  async updateVariable(
    variableId: string | number,
    payload: {
      name?: string;
      conceptId?: string | number | null;
      comparator?: 'greater_than' | 'less_than' | 'equal' | 'less_or_equal' | 'greater_or_equal' | 'not_equal';
      threshold?: string | number;
      weight?: string | number;
      dataSourceId?: string | number | null;
      conceptRules?: Array<{
        conceptId: string | number;
        comparator: 'greater_than' | 'less_than' | 'equal' | 'less_or_equal' | 'greater_or_equal' | 'not_equal';
        threshold: string | number;
        weight: string | number;
      }>;
    },
    options?: {skipLoader?: boolean}
  ): Promise<ApiResponse<RiskVariableRow>> {
    try {
      const url = GET_URL(ENDPOINTS.RISK_VARIABLE_DETAIL, {id: variableId});
      const baseHeaders = this.buildHeaders(options?.skipLoader) ?? new HttpHeaders();
      const headers = baseHeaders.set('Content-Type', 'application/merge-patch+json');
      const body: Record<string, any> = {};
      if (payload.name !== undefined) body['name'] = payload.name;
      if (payload.conceptId === null) {
        body['concept'] = null;
      } else if (payload.conceptId !== undefined) {
        body['concept'] = this.toConceptIri(payload.conceptId);
      }
      if (payload.comparator !== undefined) body['comparator'] = payload.comparator;
      if (payload.threshold !== undefined) body['threshold'] = String(payload.threshold);
      if (payload.weight !== undefined) body['weight'] = String(payload.weight);
      if (payload.dataSourceId === null) {
        body['dataSource'] = null;
      } else if (payload.dataSourceId !== undefined) {
        body['dataSource'] = this.toDataSourceIri(payload.dataSourceId);
      }
      if (Array.isArray(payload.conceptRules)) {
        body['conceptRules'] = payload.conceptRules.map(rule => ({
          conceptId: rule.conceptId,
          comparator: rule.comparator,
          threshold: String(rule.threshold),
          weight: String(rule.weight),
        }));
      }
      const response: any = await this.apiService.patch(url, body, {headers, json: false});
      if (!response) {
        return {success: true};
      }
      return {success: true, result: this.mapVariable(response)};
    } catch (error: any) {
      log.error('Error updating risk variable:', error);
    }
    return {success: false, message: 'No se pudo actualizar la variable.'};
  }

  async deleteDataSource(dataSourceId: string | number, options?: {skipLoader?: boolean}): Promise<ApiResponse<undefined>> {
    try {
      const url = GET_URL(ENDPOINTS.RISK_DATA_SOURCE_DETAIL, {id: dataSourceId});
      const headers = this.buildHeaders(options?.skipLoader);
      await this.apiService.delete(url, headers ? {headers} : undefined);
      return {success: true};
    } catch (error: any) {
      log.error('Error deleting risk data source:', error);
    }
    return {success: false, message: 'No se pudo eliminar la fuente de datos.'};
  }

  async deleteVariable(variableId: string | number, options?: {skipLoader?: boolean}): Promise<ApiResponse<undefined>> {
    try {
      const url = GET_URL(ENDPOINTS.RISK_VARIABLE_DETAIL, {id: variableId});
      const headers = this.buildHeaders(options?.skipLoader);
      await this.apiService.delete(url, headers ? {headers} : undefined);
      return {success: true};
    } catch (error: any) {
      log.error('Error deleting risk variable:', error);
    }
    return {success: false, message: 'No se pudo eliminar la variable.'};
  }

  private mapConcept(item: any): RiskConceptRow {
    return {
      id: item.id,
      name: item.name || item.title || 'Sin nombre',
      description: item.description || '',
      inherentRisk: item.inherentRisk || item.inherent || item.riskInherent || '',
      residualRisk: item.residualRisk || item.residual || item.riskResidual || '',
      approvedRisk: item.approvedRisk || item.approved || '',
      declaredRisk: item.declaredRisk || item.declared || '',
      crimes: Array.isArray(item.crimes)
        ? item.crimes.map((crime: any) => ({id: crime.id, name: crime.name || crime.title || 'Delito'}))
        : [],
      variables: Array.isArray(item.variables)
        ? item.variables.map((variable: any) => this.mapVariable(variable))
        : [],
      evaluationsCount: Number.isFinite(Number(item.evaluationsCount))
        ? Number(item.evaluationsCount)
        : Number.isFinite(Number(item.evaluations_count))
          ? Number(item.evaluations_count)
          : 0
    };
  }

  private mapDeclaration(item: any): RiskDeclarationRow {
    const booleanAnswer = item.booleanAnswer ?? item.boolean_answer ?? item.boolean ?? undefined;
    const threshold = item.threshold ?? item.declaredValue ?? item.declared_value ?? item.value ?? undefined;
    const declaredValue = item.type === 'numeric'
      ? (threshold !== undefined && threshold !== null ? String(threshold) : '')
      : (booleanAnswer === 'no' || booleanAnswer === false || booleanAnswer === 0 || booleanAnswer === '0' ? 'No' : 'Sí');

    return {
      id: item.id,
      name: item.name || item.title || 'Sin nombre',
      description: item.description || '',
      type: item.type || 'boolean',
      variableId: item.variableId
        ?? this.extractResourceId(item.variable)
        ?? (Array.isArray(item.variableIds) && item.variableIds.length ? item.variableIds[0] : undefined),
      variableName: item.variableName
        || (Array.isArray(item.variableNames) && item.variableNames.length ? item.variableNames[0] : ''),
      comparator: item.comparator || undefined,
      threshold: threshold !== undefined && threshold !== null ? String(threshold) : undefined,
      booleanAnswer,
      declaredValue,
      conceptIds: Array.isArray(item.conceptIds) ? item.conceptIds : [],
      conceptNames: Array.isArray(item.conceptNames) ? item.conceptNames : [],
    };
  }

  private mapVariable(item: any): RiskVariableRow {
    const controls = Array.isArray(item.controls)
      ? item.controls
          .map((control: any) => control?.name || control?.title || '')
          .filter((name: string) => !!name)
      : [];

    return {
      id: item.id,
      name: item.name || item.title || 'Sin nombre',
      conceptId: this.extractResourceId(item.concept)
        ?? this.extractResourceId(item.riskConcept)
        ?? item.conceptId
        ?? item.concept_id,
      dataSourceId: this.extractResourceId(item.dataSource)
        ?? this.extractResourceId(item.source)
        ?? item.dataSourceId
        ?? item.datasourceId
        ?? item.data_source_id,
      comparator: item.comparator || item.operator || undefined,
      conceptRules: Array.isArray(item.conceptRules)
        ? item.conceptRules
            .map((rule: any) => ({
              conceptId: rule.conceptId ?? rule.concept_id,
              conceptName: rule.conceptName ?? rule.concept_name,
              comparator: rule.comparator || 'greater_than',
              threshold: String(rule.threshold ?? ''),
              weight: String(rule.weight ?? ''),
              active: typeof rule.active === 'boolean' ? rule.active : true,
            }))
            .filter((rule: any) => rule.conceptId !== undefined && rule.conceptId !== null)
        : [],
      source: item.source?.name || item.source || item.dataSource?.name || item.datasource || '',
      type: this.mapVariableType(item.type || item.kind || ''),
      function: item.function || item.ruleFunction || '',
      application: item.application || item.ruleApplication || '',
      threshold: item.threshold || item.umbral || item.limit || '',
      weight: item.weight || item.riskWeight || '',
      individual: item.individual || item.individualRisk || '',
      controlled: item.controlled || item.controlledRisk || '',
      control: item.control?.name || item.controlName || controls.join(', '),
      active: typeof item.active === 'boolean' ? item.active : true
    };
  }

  private mapCrime(item: any): RiskCrimeRow {
    return {
      id: item.id,
      name: item.name || 'Delito',
      article: item.article || '',
      description: item.description || '',
    };
  }

  private mapImpact(item: any): RiskImpactRow {
    return {
      id: item.id,
      conceptId: this.extractResourceId(item.concept) ?? item.conceptId,
      crimeId: this.extractResourceId(item.crime) ?? item.crimeId,
      punitive: String(item.punitive || ''),
      operative: String(item.operative || ''),
      reputational: String(item.reputational || ''),
      financial: String(item.financial || ''),
    };
  }

  private extractResourceId(value: any): string | number | undefined {
    if (!value) return undefined;
    if (typeof value === 'object' && value.id !== undefined) {
      return value.id;
    }
    if (typeof value === 'object' && typeof value['@id'] === 'string') {
      const parts = value['@id'].split('/').filter(Boolean);
      return parts[parts.length - 1];
    }
    if (typeof value === 'string') {
      if (value.startsWith('/api/')) {
        const parts = value.split('/').filter(Boolean);
        return parts[parts.length - 1];
      }
      return value;
    }
    if (typeof value === 'number') {
      return value;
    }
    return undefined;
  }

  private mapVariableType(value: string): string {
    const normalized = String(value || '').trim().toLowerCase();
    if (!normalized) return '';
    if (normalized === 'manual') return 'Manual';
    if (normalized === 'automatic') return 'Automatica';
    if (normalized === 'qualitative') return 'Cualitativa';
    if (normalized === 'quantitative') return 'Cuantitativa';
    if (normalized === 'greater_than') return 'Mayor que';
    if (normalized === 'less_than') return 'Menor que';
    if (normalized === 'equal') return 'Igual';
    if (normalized === 'less_or_equal') return 'Menor o igual';
    if (normalized === 'greater_or_equal') return 'Mayor o igual';
    if (normalized === 'not_equal') return 'Distinto';
    return value;
  }

  private mapHeatmap(item: any): RiskHeatmap {
    const labels = {
      x: this.normalizeAxisLabel(item?.labels?.x, this.defaultHeatmapLabels.x),
      y: this.normalizeAxisLabel(item?.labels?.y, this.defaultHeatmapLabels.y),
    };

    const attributes = {
      x: this.normalizeAxisAttributes(item?.attributes?.x, this.defaultHeatmapAttributes.x),
      y: this.normalizeAxisAttributes(item?.attributes?.y, this.defaultHeatmapAttributes.y),
    };

    const matrixFromResponse = this.asNumericMatrix(item?.values);
    if (matrixFromResponse) {
      return {labels, attributes, values: matrixFromResponse};
    }

    const points = Array.isArray(item?.values) ? item.values as RiskHeatmapPoint[] : [];
    const normalizedPoints = points
      .map(point => ({
        conceptId: point?.conceptId,
        conceptName: point?.conceptName,
        crimeId: (point as any)?.crimeId,
        crimeName: (point as any)?.crimeName,
        probability: this.toNumber(point?.probability),
        impact: this.toNumber(point?.impact),
      }))
      .filter(point => point.probability > 0 && point.impact > 0);
    return {
      labels,
      attributes,
      values: this.pointsToMatrix(points, attributes.y.length, attributes.x.length),
      points: normalizedPoints
    };
  }

  private asNumericMatrix(values: any): number[][] | null {
    if (!Array.isArray(values) || !values.length) {
      return null;
    }

    if (!Array.isArray(values[0])) {
      return null;
    }

    const matrix = values.map((row: any) =>
      Array.isArray(row)
        ? row.map((value: any) => this.toNumber(value))
        : []
    );

    const hasData = matrix.some(row => row.length > 0);
    return hasData ? matrix : null;
  }

  private pointsToMatrix(points: RiskHeatmapPoint[], rows: number, cols: number): number[][] {
    const safeRows = Math.max(1, rows || 0);
    const safeCols = Math.max(1, cols || 0);
    const matrix = Array.from({length: safeRows}, () => Array.from({length: safeCols}, () => 0));

    points.forEach(point => {
      const y = this.scaleToIndex(this.toNumber(point?.probability), safeRows);
      const x = this.scaleToIndex(this.toNumber(point?.impact), safeCols);
      matrix[y][x] += 1;
    });

    return matrix;
  }

  private scaleToIndex(value: number, size: number): number {
    if (size <= 1) return 0;
    if (value <= 0) return 0;
    const normalized = (value - 1) / 4; // Scale expected 1..5 to 0..1
    const index = Math.ceil(normalized * (size - 1));
    return Math.max(0, Math.min(size - 1, index));
  }

  private toNumber(value: any): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private normalizeAxisLabel(value: any, fallback: string): string {
    const normalized = String(value || '').trim().toLowerCase();
    if (!normalized) return fallback;
    if (normalized === 'impact') return 'Impacto';
    if (normalized === 'probability') return 'Probabilidad';
    return String(value).trim();
  }

  private normalizeAxisAttributes(value: any, fallback: string[]): string[] {
    if (!Array.isArray(value) || !value.length) {
      return fallback;
    }

    const normalized = value
      .map(item => String(item || '').trim())
      .filter(item => !!item);

    if (!normalized.length) {
      return fallback;
    }

    return normalized.map(item => {
      const key = item.toLowerCase();
      if (key === 'very low' || key === 'very_low') return 'Muy bajo';
      if (key === 'low') return 'Bajo';
      if (key === 'medium') return 'Medio';
      if (key === 'high') return 'Alto';
      if (key === 'very high' || key === 'very_high') return 'Muy alto';
      return item;
    });
  }

  private toConceptIri(conceptId: string | number): string {
    if (typeof conceptId === 'string' && conceptId.startsWith('/api/')) {
      return conceptId;
    }
    return `/api/v1/risk/concepts/${conceptId}`;
  }

  private toCrimeIri(crimeId: string | number): string {
    if (typeof crimeId === 'string' && crimeId.startsWith('/api/')) {
      return crimeId;
    }
    return `/api/v1/crimes/${crimeId}`;
  }

  private toDataSourceIri(dataSourceId: string | number): string {
    if (typeof dataSourceId === 'string' && dataSourceId.startsWith('/api/')) {
      return dataSourceId;
    }
    return `/api/v1/risk/data-sources/${dataSourceId}`;
  }

  private buildHeaders(skipLoader = false): HttpHeaders | null {
    if (this.authState.isAdmin()) {
      if (!skipLoader) return null;
      return new HttpHeaders().set('X-Skip-Loader', '1');
    }

    const companyId = this.companyState.membership()?.company?.id;
    if (!companyId) {
      if (!skipLoader) return null;
      return new HttpHeaders().set('X-Skip-Loader', '1');
    }

    let headers = new HttpHeaders().set('X-Selected-Company-ID', String(companyId));
    if (skipLoader) {
      headers = headers.set('X-Skip-Loader', '1');
    }
    return headers;
  }
}
